import axios from 'axios';
import _chunk from 'lodash/chunk';
import _filter from 'lodash/filter';
import _flatMap from 'lodash/flatMap';
import _uniq from 'lodash/uniq';
import _map from 'lodash/map';
import _compact from 'lodash/compact';
import _isEmpty from 'lodash/isEmpty';
import {
  productsQuery, searchFulfillmentQuery, searchcollectionSkusQuery, metadataQuery, searchcollectionSkuesQueryByItemIds
} from './queries';
import { headers, collectionHeaders } from './_config';
import logNewRelicCustom from '../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../../constants';

const asyncLogNullFulfillment = async (data, zipCode, storeId) => {
  const logData = _map(data, (response) => {
    const missingFulfillment = _map(
      response?.data?.data?.products,
      (product) => (!product?.fulfillment?.fulfillmentOptions
        ? product.itemId
        : null)
    );
    return ({
      zipCode,
      storeId,
      traceId: response?.headers?.['x-cloud-trace-context'],
      fulfillmentErrors: _compact(missingFulfillment)
    });
  });
  const filteredLogData = _filter(logData, (log) => log.fulfillmentErrors.length);

  if (filteredLogData.length) {
    logNewRelicCustom(NEW_RELIC_EVENTS.FULFILLMENT_ERRORS, { logData: filteredLogData });
  }
};

export const productsApi = async (host, batchSize, itemIds, zipCode) => {
  const uniqueItemIds = _uniq(itemIds);
  const batches = _chunk(
    _filter(uniqueItemIds, (itemId) => itemId),
    batchSize
  );

  const batchedGets = batches.map(
    (batch) => axios.post(
      host,
      { query: productsQuery,
        variables: {
          itemIds: batch,
          zipCode
        } },
      headers
    )
  );
  if (batchedGets.length > 0) {
    const data = await axios.all(batchedGets);
    const flattened = _flatMap(data, ({ data: { data: { products } } }) => products);
    return flattened;
  }
  return [];
};

export const fulfillmentApi = async (host, batchSize, itemIds, storeId, zipCode) => {
  const uniqueItemIds = _uniq(itemIds);
  const batches = _chunk(
    _filter(uniqueItemIds, (itemId) => itemId),
    batchSize
  );

  const batchedGets = batches.map(
    (batch) => axios.post(
      host,
      {
        query: searchFulfillmentQuery,
        variables: {
          storefilter: 'ALL',
          channel: 'DESKTOP',
          additionalSearchParams: {
            deliveryZip: zipCode,
            multiStoreIds: []
          },
          itemIds: batch,
          pageSize: batchSize,
          startIndex: 0,
          storeId
        }
      },
      headers
    )
  );

  if (batchedGets.length > 0) {
    const data = await axios.all(batchedGets);
    const flattened = _flatMap(data, ({ data: { data: { searchModel: { products } } } }) => products);
    asyncLogNullFulfillment(data, zipCode, storeId);
    return flattened;
  }
  return [];
};

export const searchcollectionSkusAfromnavparam = async (host, batchSize, storeId, zipCode, navParam) => {
  if (!navParam || navParam.length === 0) {
    const errorMessage = 'No navigation parameters provided';
    logNewRelicCustom(NEW_RELIC_EVENTS.SEARCH_COLLECTION_SKUS_FROM_NAVPARAM_ERROR, {
      searchCollectionSkusFromNavParam: {
        api: host,
        errorMessage,
        navParams: navParam
      }
    });
    throw new Error(errorMessage);
  }

  const getCollectionPromises = navParam.map(async (param) => {
    const trimmedNavParam = param.startsWith('N-') ? param.substring(2) : param;
    const response = await axios.post(
      host,
      {
        operationName: 'searchModel',
        query: searchcollectionSkusQuery, // Ensure searchcollectionSkusQuery is defined
        variables: {
          skipBuyitagain: true,
          skipDiscoveryZones: false,
          skipFavoriteCount: false,
          skipInstallServices: false,
          storefilter: 'ALL',
          channel: 'DESKTOP',
          filter: {},
          additionalSearchParams: {
            deliveryZip: zipCode,
            multiStoreIds: []
          },
          navParam: trimmedNavParam,
          orderBy: {
            field: 'TOP_SELLERS',
            order: 'ASC'
          },
          pageSize: batchSize,
          startIndex: 0,
          storeId // Use the provided storeId parameter
        }
      },
      { headers: collectionHeaders } // Pass headers as config object to axios.post
    );
    if (response.data.errors) {
      const errorMessages = [];
      response.data.errors.forEach((error) => {
        errorMessages.push(error.message);
      });
      logNewRelicCustom(NEW_RELIC_EVENTS.SEARCH_COLLECTION_SKUS_FROM_NAVPARAM_ERROR, {
        searchCollectionSkusFromNavParam: {
          api: host,
          errorMessages,
          navParam: trimmedNavParam
        }
      });
    }
    return response.data; // Return the data part of the axios response
  });

  const responses = await Promise.allSettled(getCollectionPromises);

  const successfulResponses = responses
    .filter(({ status }) => status === 'fulfilled')
    .map(({ value }) => value); // Extract successful responses

  if (successfulResponses.length === 0) {
    const errorMessage = 'Not enough successful API calls to proceed';
    logNewRelicCustom(NEW_RELIC_EVENTS.SEARCH_COLLECTION_SKUS_FROM_NAVPARAM_ERROR, {
      searchCollectionSkusFromNavParam: {
        api: host,
        errorMessage,
        navParams: navParam
      }
    });
    throw new Error(errorMessage);
  }

  const result = successfulResponses.reduce((accumulator, { data: { searchModel } }) => {
    if (searchModel) {
      const { products = [], appliedDimensions = [] } = searchModel;

      accumulator.choicebasedProducts.push(...(products ?? []));
      accumulator.choicebasedDimensions.push(...(appliedDimensions ?? []));
    }
    return accumulator;
  }, { choicebasedProducts: [], choicebasedDimensions: [] }); // <-- Initial value with empty arrays

  return result;
};

export const getproductsMetadata = async (host, parentskus) => {
  try {
    const getProductsMetadataPromises = parentskus.map(async (parentId) => {
      const response = await axios.post(
        host,
        {
          operationName: 'metadata',
          query: metadataQuery,
          variables: {
            parentId
          }
        },
        { headers: collectionHeaders }
      );

      if (_isEmpty(response?.data?.data?.metadata)) {
        const errorMessage = `MetaData is Null for the ParentSku ${parentId}`;
        logNewRelicCustom(NEW_RELIC_EVENTS.PRODUCTS_METADATA, {
          productsMetadata: {
            api: host,
            parentId,
            errorMessage,
          }
        });
      }
      return { parentId, metadata: response.data.data.metadata }; // Return parentId along with metadata
    });

    const responses = await Promise.allSettled(getProductsMetadataPromises);

    const resultsByParentId = {};

    responses.forEach((promiseResult) => {
      const { parentId, metadata } = promiseResult.value;
      if (promiseResult.status === 'fulfilled') {
        resultsByParentId[parentId] = metadata;
      } else {
        logNewRelicCustom(NEW_RELIC_EVENTS.PRODUCTS_METADATA, {
          productsMetadata: {
            api: host,
            parentId,
            errorMessage: promiseResult.reason,
          }
        });
        // eslint-disable-next-line no-console
        console.error('Error fetching metadata:', promiseResult.reason);
      }
    });

    // Return a promise that resolves to the aggregated results mapped by parentId
    return Promise.resolve(resultsByParentId);
  } catch (error) {
    logNewRelicCustom(NEW_RELIC_EVENTS.PRODUCTS_METADATA, {
      productsMetadata: {
        api: host,
        parentskus,
        errorMessage: error,
      }
    });
    // eslint-disable-next-line no-console
    console.error('Error fetching metadata:', error);
    throw error; // Re-throw the error to propagate it if needed
  }
};

export const searchcollectionSkus = async (host, batchSize, storeId, zipCode, skus) => {
  try {

    // Make a single API call with all skus
    const response = await axios.post(
      host,
      {
        operationName: 'searchModel',
        query: searchcollectionSkuesQueryByItemIds,
        variables: {
          skipInstallServices: true,
          skipKPF: false,
          skipSpecificationGroup: false,
          skipSubscribeAndSave: true,
          itemIds: skus,
          storeId,
          storefilter: 'ALL'
        }
      },
      { headers: collectionHeaders }
    );

    const responseData = response.data; // Assuming response has an additional data wrapper
    if (!responseData || !responseData.data || !responseData.data.searchModel) {
      logNewRelicCustom(NEW_RELIC_EVENTS.SEARCH_COLLECTION_SKUS, {
        searchCollectionSkus: {
          api: host,
          skus,
          errorMessage: responseData,
        }
      });
      // eslint-disable-next-line no-console
      console.error('Response missing expected data or searchModel:', responseData);
      throw new Error('Response missing expected data or searchModel');
    }

    const { searchModel } = responseData.data;
    const { products = [] } = searchModel;

    return products;
  } catch (error) {
    logNewRelicCustom(NEW_RELIC_EVENTS.SEARCH_COLLECTION_SKUS, {
      searchCollectionSkus: {
        api: host,
        skus,
        errorMessage: error,
      }
    });
    // eslint-disable-next-line no-console
    console.error('Error fetching collection SKUs:', error);
    throw error; // Rethrow for higher-level handling
  }
};
